/**
 * Pending Delivery Sync Service
 * 
 * Handles synchronization of pending deliveries between DexieDB (client) and MongoDB (server)
 * for offline peer messaging. Lightweight metadata only - actual content delivered via P2P.
 * 
 * Flow:
 * 1. Sender queues delivery locally when recipient is offline
 * 2. Sync service pushes metadata to MongoDB
 * 3. When recipient comes online, they fetch pending deliveries from MongoDB
 * 4. Actual content transferred via P2P
 * 5. Delivery marked complete, cleaned up after grace period
 */

import type { MobyDB, PendingDeliveryEntity, PendingDeliveryType } from './db';

export interface PendingDeliverySyncConfig {
  serverUrl: string;           // Email gateway / API server URL
  userAddress: string;         // This user's hex public key (64 hex chars)
  secret: string;              // Hex private key for Ed25519 signing
}

export interface ServerPendingDelivery {
  _id: string;
  recipientAddress: string;
  senderAddress: string;
  deliveryType: PendingDeliveryType;
  metadata: PendingDeliveryEntity['metadata'];
  createdAt: string;
  expiresAt: string;
}

// --- Encoding helpers (no external deps) ---
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Generate auth headers matching the gateway's verifySignature middleware.
 * Gateway expects:
 *   X-Moby-Address:   base64-encoded 32-byte Ed25519 public key
 *   X-Moby-Timestamp: Unix ms string
 *   X-Moby-Signature: base64-encoded Ed25519 detached signature of (timestamp + bodyString)
 */
async function getAuthHeaders(
  config: PendingDeliverySyncConfig,
  bodyString: string = ''
): Promise<Record<string, string>> {
  const { ed25519 } = await import('@noble/curves/ed25519');

  const timestamp = Date.now().toString();
  const message = `${timestamp}${bodyString}`;
  const messageBytes = new TextEncoder().encode(message);

  const privateKeyBytes = hexToBytes(config.secret);
  const publicKeyBytes = ed25519.getPublicKey(privateKeyBytes);
  const signatureBytes = ed25519.sign(messageBytes, privateKeyBytes);

  return {
    'Content-Type': 'application/json',
    'X-Moby-Address': bytesToBase64(publicKeyBytes),
    'X-Moby-Timestamp': timestamp,
    'X-Moby-Signature': bytesToBase64(signatureBytes),
  };
}

/**
 * Push local pending deliveries to MongoDB server
 * Called periodically or when new deliveries are queued
 */
export async function syncPendingDeliveriesToServer(
  db: MobyDB,
  config: PendingDeliverySyncConfig
): Promise<{ synced: number; errors: string[] }> {
  const errors: string[] = [];
  let synced = 0;

  try {
    const unsynced = await db.getUnsyncedPendingDeliveries();
    
    if (unsynced.length === 0) {
      return { synced: 0, errors: [] };
    }

    console.log(`[PendingSync] Syncing ${unsynced.length} pending deliveries to server`);

    for (const delivery of unsynced) {
      try {
        const bodyObj = {
          recipientAddress: delivery.recipientAddress,
          senderAddress: delivery.senderAddress,
          deliveryType: delivery.deliveryType,
          metadata: delivery.metadata,
          expiresAt: delivery.expiresAt.toISOString(),
        };
        const bodyStr = JSON.stringify(bodyObj);
        const headers = await getAuthHeaders(config, bodyStr);
        
        const response = await fetch(`${config.serverUrl}/api/email/pending`, {
          method: 'POST',
          headers,
          body: bodyStr,
        });

        if (!response.ok) {
          const errorText = await response.text();
          errors.push(`Failed to sync ${delivery.id}: ${response.status} - ${errorText}`);
          continue;
        }

        const result = await response.json();
        
        if (result.success && result.id) {
          await db.markPendingDeliverySynced(delivery.id, result.id);
          synced++;
          console.log(`[PendingSync] Synced delivery ${delivery.id.slice(0, 8)}... -> server ${result.id}`);
        }
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        errors.push(`Error syncing ${delivery.id}: ${errMsg}`);
      }
    }

    return { synced, errors };
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err);
    errors.push(`Sync failed: ${errMsg}`);
    return { synced, errors };
  }
}

/**
 * Fetch pending deliveries from MongoDB server for this user
 * Called when user comes online or periodically
 */
export async function fetchPendingDeliveriesFromServer(
  db: MobyDB,
  config: PendingDeliverySyncConfig
): Promise<{ fetched: number; errors: string[] }> {
  const errors: string[] = [];
  let fetched = 0;

  try {
    const headers = await getAuthHeaders(config);
    
    const response = await fetch(`${config.serverUrl}/api/email/pending?recipientAddress=${config.userAddress}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      errors.push(`Failed to fetch pending: ${response.status} - ${errorText}`);
      return { fetched, errors };
    }

    const result = await response.json();
    
    if (!result.success || !Array.isArray(result.pending)) {
      errors.push('Invalid server response');
      return { fetched, errors };
    }

    const serverDeliveries: ServerPendingDelivery[] = result.pending;
    console.log(`[PendingSync] Fetched ${serverDeliveries.length} pending deliveries from server`);

    for (const serverDelivery of serverDeliveries) {
      try {
        // Check if we already have this delivery locally
        // NOTE: serverDeliveryId is not indexed — use filter() for full-scan dedup
        const existing = await db.pendingDeliveries
          .filter(d => d.serverDeliveryId === serverDelivery._id)
          .first();

        if (existing) {
          // Already have it, skip
          continue;
        }

        // Store locally
        const id = crypto.randomUUID();
        await db.pendingDeliveries.add({
          id,
          recipientAddress: serverDelivery.recipientAddress,
          senderAddress: serverDelivery.senderAddress,
          deliveryType: serverDelivery.deliveryType,
          metadata: serverDelivery.metadata,
          createdAt: new Date(serverDelivery.createdAt),
          expiresAt: new Date(serverDelivery.expiresAt),
          delivered: 0,
          syncedToServer: 1, // Already on server
          serverDeliveryId: serverDelivery._id,
        });

        fetched++;
        console.log(`[PendingSync] Stored server delivery ${serverDelivery._id} locally as ${id.slice(0, 8)}...`);

        // CALENDAR INVITE: Create invite record directly from metadata
        // The metadata contains enough info to show the invite in the UI
        if (serverDelivery.deliveryType === 'calendar_invite' && serverDelivery.metadata?.eventId) {
          try {
            const m = serverDelivery.metadata;
            const existingInvite = await db.calendarInvites
              .where('eventId')
              .equals(m.eventId!)
              .filter(inv => inv.recipientAddress === serverDelivery.recipientAddress)
              .first();

            if (!existingInvite) {
              await db.calendarInvites.add({
                id: crypto.randomUUID(),
                eventId: m.eventId!,
                senderAddress: serverDelivery.senderAddress,
                recipientAddress: serverDelivery.recipientAddress,
                rsvp: 'pending',
                sentAt: new Date(serverDelivery.createdAt).getTime(),
                deliveredP2P: 0,
                pendingDeliveryId: id,
                eventSnapshot: JSON.stringify({
                  title: m.eventTitle || 'Meeting',
                  startTime: m.startTime || 0,
                  endTime: m.endTime || 0,
                  allDay: m.allDay || false,
                  organizerName: m.organizerName,
                  groupAddress: m.groupAddress,
                  groupName: m.groupName,
                }),
              } as any);

              console.log(`[PendingSync] Created calendar invite from pending delivery`, {
                eventId: m.eventId,
                eventTitle: m.eventTitle,
              });

              // Notify UI
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('calendar-invite-received', {
                  detail: { eventId: m.eventId, inviteId: id },
                }));
              }
            }
          } catch (calErr) {
            console.error('[PendingSync] Failed to create calendar invite from pending:', calErr);
          }
        }
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        errors.push(`Error storing ${serverDelivery._id}: ${errMsg}`);
      }
    }

    return { fetched, errors };
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err);
    errors.push(`Fetch failed: ${errMsg}`);
    return { fetched, errors };
  }
}

/**
 * Mark a delivery as completed on the server
 * Called after P2P delivery succeeds
 */
export async function markDeliveryCompletedOnServer(
  deliveryId: string,
  serverDeliveryId: string,
  config: PendingDeliverySyncConfig
): Promise<boolean> {
  try {
    const headers = await getAuthHeaders(config);
    
    const response = await fetch(`${config.serverUrl}/api/email/pending/${serverDeliveryId}/delivered`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      console.error(`[PendingSync] Failed to mark ${serverDeliveryId} as delivered: ${response.status}`);
      return false;
    }

    console.log(`[PendingSync] Marked server delivery ${serverDeliveryId} as completed`);
    return true;
  } catch (err) {
    console.error(`[PendingSync] Error marking delivery completed:`, err);
    return false;
  }
}

/**
 * Process pending deliveries for a specific peer who just came online
 * Returns deliveries that need P2P transfer
 */
export async function getDeliveriesForOnlinePeer(
  db: MobyDB,
  peerAddress: string
): Promise<PendingDeliveryEntity[]> {
  return await db.getPendingDeliveriesForRecipient(peerAddress);
}

/**
 * Create a sync manager that handles periodic sync and cleanup
 */
export function createPendingDeliverySyncManager(
  db: MobyDB,
  config: PendingDeliverySyncConfig,
  options: {
    syncIntervalMs?: number;    // How often to sync to server (default: 30s)
    cleanupIntervalMs?: number; // How often to cleanup (default: 1hr)
  } = {}
) {
  const syncIntervalMs = options.syncIntervalMs ?? 30000;
  const cleanupIntervalMs = options.cleanupIntervalMs ?? 3600000;

  let syncInterval: ReturnType<typeof setInterval> | null = null;
  let cleanupInterval: ReturnType<typeof setInterval> | null = null;
  let isRunning = false;

  const sync = async () => {
    if (!isRunning) return;
    
    try {
      // Push local deliveries to server
      const pushResult = await syncPendingDeliveriesToServer(db, config);
      if (pushResult.errors.length > 0) {
        console.warn('[PendingSync] Push errors:', pushResult.errors);
      }

      // Fetch deliveries from server
      const fetchResult = await fetchPendingDeliveriesFromServer(db, config);
      if (fetchResult.errors.length > 0) {
        console.warn('[PendingSync] Fetch errors:', fetchResult.errors);
      }
    } catch (err) {
      console.error('[PendingSync] Sync error:', err);
    }
  };

  const cleanup = async () => {
    if (!isRunning) return;
    
    try {
      await db.cleanupExpiredPendingDeliveries();
      await db.cleanupDeliveredPendingDeliveries();
    } catch (err) {
      console.error('[PendingSync] Cleanup error:', err);
    }
  };

  return {
    start() {
      if (isRunning) return;
      isRunning = true;

      // Initial sync
      sync();

      // Set up periodic sync
      syncInterval = setInterval(sync, syncIntervalMs);
      cleanupInterval = setInterval(cleanup, cleanupIntervalMs);

      console.log('[PendingSync] Started sync manager');
    },

    stop() {
      isRunning = false;
      if (syncInterval) clearInterval(syncInterval);
      if (cleanupInterval) clearInterval(cleanupInterval);
      syncInterval = null;
      cleanupInterval = null;

      console.log('[PendingSync] Stopped sync manager');
    },

    // Manual sync trigger
    sync,

    // Manual cleanup trigger
    cleanup,

    isRunning: () => isRunning,
  };
}
